// components/patient/OrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { orderService } from '../../services/orderService';
import { DB } from '../../utils/db';

function mapBackendStatus(order) {
  const status = (order.orderStatus || '').toUpperCase();
  if (status === 'CANCELED') return 'canceled';
  if (status === 'COMPLETED' || status === 'CLOSED') {
    const hasDelivery = (order.items || []).some(item =>
      (item.shipments || []).some(s => s.actualDeliveryDate)
    );
    if (hasDelivery || status === 'CLOSED') return 'delivered';
    return 'shipped';
  }
  if (status === 'OPEN') {
    const hasProcessing = (order.histories || []).some(h =>
      (h.event || '').toLowerCase().includes('process') ||
      (h.message || '').toLowerCase().includes('fulfil')
    );
    return hasProcessing ? 'processing' : 'submitted';
  }
  return 'submitted';
}

export function OrderDetail({ orderId, onNavigate }) {
  const { brand } = useBrand();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApiOrder, setIsApiOrder] = useState(false);
  
  useEffect(() => {
    loadData();
  }, [orderId]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const numericId = parseInt(orderId, 10);
      if (!isNaN(numericId)) {
        const result = await orderService.getOrders({ id: numericId });
        const hits = result?.hits || [];
        if (hits.length > 0) {
          const apiOrder = hits[0];
          const trackingNumbers = [];
          (apiOrder.items || []).forEach(item => {
            (item.shipments || []).forEach(s => {
              if (s.trackingNumber) trackingNumbers.push(s.trackingNumber);
            });
          });
          const timeline = [];
          if (apiOrder.startDate) timeline.push({ status: 'submitted', timestamp: apiOrder.startDate });
          (apiOrder.histories || []).forEach(h => {
            if (h.createdAt) timeline.push({ status: h.status || 'update', timestamp: h.createdAt, note: h.message });
          });

          setOrder({
            id: apiOrder.id,
            status: mapBackendStatus(apiOrder),
            createdAt: apiOrder.startDate,
            items: apiOrder.items || [],
            totals: apiOrder.totals || {},
            trackingNumber: trackingNumbers[0] || null,
            timeline: timeline.length > 0 ? timeline : [{ status: 'submitted', timestamp: apiOrder.startDate }],
            shipping: apiOrder.shipping,
            payment: apiOrder.payment,
            notes: apiOrder.notes || [],
            shippingMethod: apiOrder.totals?.shipping > 0 ? 'express' : 'standard',
            _raw: apiOrder
          });
          setIsApiOrder(true);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.warn('Failed to load order from API, trying localStorage:', err.message);
    }

    const allOrders = await DB.get('d2p_orders') || [];
    const foundOrder = allOrders.find(o => o.id === orderId);
    if (foundOrder) {
      if (!foundOrder.items && foundOrder.prescriptionIds) {
        const allRxs = await DB.get('d2p_prescriptions') || [];
        const orderRxs = allRxs.filter(rx => foundOrder.prescriptionIds.includes(rx.id));
        foundOrder._prescriptions = orderRxs;
      }
      setOrder(foundOrder);
      setIsApiOrder(false);
    }
    setLoading(false);
  };
  
  if (loading) {
    return (
      <div style={{
        background: '#F9FAFB',
        minHeight: '100vh',
        padding: '40px 24px',
        fontFamily: 'Libre Franklin, sans-serif'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center', paddingTop: 100 }}>
          <div style={{
            width: 48, height: 48,
            border: '4px solid #E5E7EB',
            borderTop: `4px solid ${brand.c.accent}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6B7280' }}>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{
        background: '#F9FAFB',
        minHeight: '100vh',
        padding: '40px 24px',
        fontFamily: 'Libre Franklin, sans-serif'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <p>Order not found</p>
          <Button onClick={() => onNavigate('orders')}>
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }
  
  const statusConfig = {
    submitted: { icon: 'clipboard', color: '#6B7280', label: 'Submitted' },
    processing: { icon: 'settings', color: brand.c.accent, label: 'Processing' },
    shipped: { icon: 'truck', color: '#7C3AED', label: 'In Transit' },
    delivered: { icon: 'checkCircle', color: '#10B981', label: 'Delivered' },
    canceled: { icon: 'x', color: '#EF4444', label: 'Canceled' }
  };
  
  const currentStatus = statusConfig[order.status] || statusConfig.submitted;
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('orders')}
          style={{ marginBottom: 24 }}
        >
          Back to Orders
        </Button>
        
        {/* Header Card */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'start', marginBottom: 24 }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 16,
              background: `${currentStatus.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Icon name={currentStatus.icon} size={36} color={currentStatus.color} />
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap'
              }}>
                <h1 style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: brand.c.navy,
                  margin: 0
                }}>
                  Order {order.id}
                </h1>
                <Badge status={order.status} />
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
                fontSize: 14,
                color: '#6B7280'
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Order Placed
                  </div>
                  <div>{new Date(order.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Items
                  </div>
                  <div>{isApiOrder ? (order.items || []).length : (order.items?.length || order.prescriptionIds?.length || 0)} item(s)</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Shipping Method
                  </div>
                  <div>{order.shippingMethod === 'express' ? 'Express (1-2 days)' : 'Standard (2-3 days)'}</div>
                </div>
              </div>
            </div>
          </div>
          
          {order.trackingNumber && (
            <div style={{
              padding: 16,
              background: '#EFF6FF',
              borderRadius: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1E40AF', marginBottom: 4 }}>
                  Tracking Number
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#3B82F6', fontFamily: 'monospace' }}>
                  {order.trackingNumber}
                </div>
              </div>
              <Button variant="accent" size="sm" icon="truck">
                Track Package
              </Button>
            </div>
          )}

          {(order.poNumber || order.glCode || order.location) && (
            <div style={{
              marginTop: 16,
              padding: 16,
              background: '#F0FDF4',
              borderRadius: 8,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              fontSize: 14
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#065F46', gridColumn: '1 / -1', marginBottom: 4 }}>
                Business Information
              </div>
              {order.poNumber && (
                <div>
                  <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>PO Number</div>
                  <div style={{ color: '#047857' }}>{order.poNumber}</div>
                </div>
              )}
              {order.glCode && (
                <div>
                  <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>GL Code</div>
                  <div style={{ color: '#047857' }}>{order.glCode}</div>
                </div>
              )}
              {order.location && (
                <div>
                  <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 2 }}>Location / Facility</div>
                  <div style={{ color: '#047857' }}>{order.location}</div>
                </div>
              )}
            </div>
          )}
        </Card>
        
        {/* Timeline */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Order Timeline
          </h2>
          
          <div style={{ position: 'relative' }}>
            {order.timeline && order.timeline.map((event, i) => {
              const isLast = i === order.timeline.length - 1;
              const config = statusConfig[event.status] || statusConfig.submitted;
              
              return (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: isLast ? 0 : 32 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: config.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Icon name={config.icon} size={24} />
                    </div>
                    {!isLast && (
                      <div style={{
                        width: 2,
                        flex: 1,
                        background: '#E5E7EB',
                        marginTop: 8,
                        marginBottom: 8,
                        minHeight: 40
                      }} />
                    )}
                  </div>
                  
                  <div style={{ flex: 1, paddingTop: 12 }}>
                    <div style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: brand.c.navy,
                      marginBottom: 4,
                      textTransform: 'capitalize'
                    }}>
                      {config.label}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      {new Date(event.timestamp).toLocaleString()}
                    </div>
                    {event.note && (
                      <div style={{
                        marginTop: 8,
                        fontSize: 14,
                        color: '#374151',
                        fontStyle: 'italic'
                      }}>
                        {event.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        
        {/* Order Items */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
            Order Items
          </h2>
          
          <div style={{ display: 'grid', gap: 16 }}>
            {(isApiOrder || (order.items && order.items.length > 0)) ? (
              (order.items || []).map((item, i) => (
                <div
                  key={item.id || i}
                  style={{
                    padding: 16,
                    background: '#F9FAFB',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: brand.c.navy }}>
                      {item.name || item.sku}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      Qty: {item.qty}{item.sku && <span style={{ marginLeft: 8 }}>SKU: {item.sku}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: brand.c.accent }}>
                      ${(item.totalPrice || item.total || (item.price * item.qty) || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      ${(item.price || 0).toFixed(2)} each
                    </div>
                  </div>
                </div>
              ))
            ) : (
              (order._prescriptions || []).map(rx => (
                <div
                  key={rx.id}
                  style={{
                    padding: 16,
                    background: '#F9FAFB',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: brand.c.navy }}>
                      {rx.medication}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      {rx.quantity}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: brand.c.accent }}>
                      $12.00
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      per fill
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div style={{
            marginTop: 20,
            paddingTop: 20,
            borderTop: '1px solid #E5E7EB'
          }}>
            {(isApiOrder || order.totals) ? (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  fontSize: 14
                }}>
                  <span style={{ color: '#6B7280' }}>Medications ({(order.items || []).length} item{(order.items || []).length !== 1 ? 's' : ''})</span>
                  <span style={{ fontWeight: 600 }}>${(order.totals?.itemSubtotal || order.totals?.subtotal || 0).toFixed(2)}</span>
                </div>
                {(order.totals?.pharmacyFee || 0) > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>Pharmacy Services Fee</span>
                    <span style={{ fontWeight: 600 }}>${(order.totals.pharmacyFee).toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  fontSize: 14
                }}>
                  <span style={{ color: '#6B7280' }}>Shipping</span>
                  <span style={{ fontWeight: 600 }}>
                    {(order.totals?.shipping || 0) > 0 ? `$${(order.totals.shipping).toFixed(2)}` : 'FREE'}
                  </span>
                </div>
                {(order.totals?.tax || 0) > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>Tax</span>
                    <span style={{ fontWeight: 600 }}>${(order.totals.tax).toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid #E5E7EB',
                  fontSize: 20,
                  fontWeight: 700
                }}>
                  <div>
                    <span>Subtotal</span>
                    {(order.totals?.pharmacyFee || 0) > 0 && (
                      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>*Includes Pharmacy Services Fee</div>
                    )}
                  </div>
                  <span style={{ color: brand.c.accent }}>
                    ${(order.totals?.subtotal || 0).toFixed(2)}
                  </span>
                </div>
                {(order.totals?.grand || 0) > 0 && order.totals?.grand !== order.totals?.subtotal && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 8,
                    fontSize: 20,
                    fontWeight: 700
                  }}>
                    <span>Total</span>
                    <span style={{ color: brand.c.accent }}>
                      ${(order.totals.grand).toFixed(2)}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                {(() => {
                  const rxCount = (order._prescriptions || []).length;
                  const medCost = rxCount * 12;
                  const feeTotal = rxCount * 4;
                  const sub = medCost + feeTotal;
                  const ship = order.shippingMethod === 'express' ? 9.99 : 0;
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                        <span style={{ color: '#6B7280' }}>Medications ({rxCount} item{rxCount !== 1 ? 's' : ''})</span>
                        <span style={{ fontWeight: 600 }}>${medCost.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                        <span style={{ color: '#6B7280' }}>Pharmacy Services Fee</span>
                        <span style={{ fontWeight: 600 }}>${feeTotal.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                        <span style={{ color: '#6B7280' }}>Shipping</span>
                        <span style={{ fontWeight: 600 }}>{ship > 0 ? `$${ship.toFixed(2)}` : 'FREE'}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: '1px solid #E5E7EB',
                        fontSize: 20,
                        fontWeight: 700
                      }}>
                        <div>
                          <span>Subtotal</span>
                          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 400 }}>*Includes Pharmacy Services Fee</div>
                        </div>
                        <span style={{ color: brand.c.accent }}>${sub.toFixed(2)}</span>
                      </div>
                      {ship > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 20, fontWeight: 700 }}>
                          <span>Total</span>
                          <span style={{ color: brand.c.accent }}>${(sub + ship).toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </Card>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button icon="msgCircle" onClick={() => onNavigate('messages')}>
            Contact Support
          </Button>
          {order.status === 'delivered' && (
            <Button variant="accent" icon="package" onClick={() => onNavigate('bundle')}>
              Reorder
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
